const terminalStatuses = new Set(['completed', 'complete', 'finished', 'all_out']);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const deriveWinProbability = ({
  currentInnings = 1,
  battingTeam = '',
  bowlingTeam = '',
  target,
  currentRuns = 0,
  ballsLeft,
  wicketsDown = 0,
  wicketsRemaining,
  oversFormat,
  playersPerSide,
  status = '',
  isMatchComplete = false,
  totalBalls,
} = {}) => {
  const inningsNumber = Number(currentInnings) || 1;
  const parsedTarget = toFiniteNumber(target);

  if (inningsNumber < 2 || !Number.isFinite(parsedTarget)) {
    return {
      show: false,
      battingTeam,
      bowlingTeam,
      battingTeamProbability: null,
      bowlingTeamProbability: null,
      isComplete: false,
      reason: 'not-a-chase',
    };
  }

  const totalBallsValue = (() => {
    const explicitTotalBalls = toFiniteNumber(totalBalls);
    if (Number.isFinite(explicitTotalBalls) && explicitTotalBalls > 0) return explicitTotalBalls;
    const oversValue = toFiniteNumber(oversFormat);
    return Number.isFinite(oversValue) && oversValue > 0 ? oversValue * 6 : null;
  })();

  const ballsLeftValue = Math.max(toFiniteNumber(ballsLeft) ?? 0, 0);
  const currentRunsValue = Math.max(toFiniteNumber(currentRuns) ?? 0, 0);
  const wicketsDownValue = Math.max(toFiniteNumber(wicketsDown) ?? 0, 0);
  const totalWickets = Math.max((toFiniteNumber(playersPerSide) ?? 11) - 1, 1);
  const wicketsRemainingValue = clamp(
    Math.max(toFiniteNumber(wicketsRemaining) ?? (totalWickets - wicketsDownValue), 0),
    0,
    totalWickets,
  );
  const normalizedStatus = String(status || '').toLowerCase();
  const runsNeeded = Math.max(parsedTarget - currentRunsValue, 0);
  const inningsClosed = isMatchComplete || terminalStatuses.has(normalizedStatus) || wicketsRemainingValue <= 0 || ballsLeftValue <= 0;

  if (runsNeeded <= 0) {
    return {
      show: true,
      battingTeam,
      bowlingTeam,
      battingTeamProbability: 100,
      bowlingTeamProbability: 0,
      isComplete: true,
      reason: 'target-achieved',
      runsNeeded,
      ballsLeft: ballsLeftValue,
      wicketsRemaining: wicketsRemainingValue,
      requiredRunRate: 0,
    };
  }

  if (inningsClosed) {
    return {
      show: true,
      battingTeam,
      bowlingTeam,
      battingTeamProbability: 0,
      bowlingTeamProbability: 100,
      isComplete: true,
      reason: 'innings-complete',
      runsNeeded,
      ballsLeft: ballsLeftValue,
      wicketsRemaining: wicketsRemainingValue,
      requiredRunRate: ballsLeftValue > 0 ? (runsNeeded / ballsLeftValue) * 6 : Infinity,
    };
  }

  const requiredRunRate = ballsLeftValue > 0 ? (runsNeeded / ballsLeftValue) * 6 : Infinity;
  const totalBallsForFactors = Number.isFinite(totalBallsValue) && totalBallsValue > 0
    ? totalBallsValue
    : Math.max(ballsLeftValue, 1);
  const targetRunRateBaseline = clamp((parsedTarget / totalBallsForFactors) * 6, 4, 12);
  const runsProgress = clamp(1 - (runsNeeded / Math.max(parsedTarget, 1)), 0, 1);
  const ballsFactor = clamp(ballsLeftValue / totalBallsForFactors, 0, 1);
  const wicketsFactor = clamp(wicketsRemainingValue / totalWickets, 0, 1);
  const manageableBonus = Math.max(Math.min(targetRunRateBaseline - requiredRunRate, 4), 0) * 3;
  const pressurePenalty = Math.max(requiredRunRate - targetRunRateBaseline, 0) * 5;

  let probability =
    50
    + ((runsProgress - 0.5) * 30)
    + ((ballsFactor - 0.5) * 18)
    + ((wicketsFactor - 0.5) * 14)
    + manageableBonus
    - pressurePenalty;

  probability = Math.round(clamp(probability, 1, 99));

  return {
    show: true,
    battingTeam,
    bowlingTeam,
    battingTeamProbability: probability,
    bowlingTeamProbability: 100 - probability,
    isComplete: false,
    reason: 'live-chase',
    runsNeeded,
    ballsLeft: ballsLeftValue,
    wicketsRemaining: wicketsRemainingValue,
    requiredRunRate,
  };
};
